import React, { useState, useEffect } from 'react';
import { Loader2, ChevronDown, AlertCircle, AlertTriangle, ImageIcon } from 'lucide-react';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../ui/accordion';
import { Badge } from '../ui/badge';
import propertyService from '../../services/propertyService';
import PropertyImageCarouselModal from './PropertyImageCarouselModal';
import { ImageInspectionRegistry, ImageInspectionRegistryItem } from '../../models/property';

interface InspectionImagesDisplayProps {
  propertyId: string;
  inspectionId: string | null;
}

// Define the type outside the component to avoid recreating it on each render
type ImageRegistryItem = {
  imageId: string;
  room: string;
  description: string;
  redFlags: string[];
  url?: string; // URL is optional until loaded
};

const InspectionImagesDisplay: React.FC<InspectionImagesDisplayProps> = ({ propertyId, inspectionId }) => {
  // State declarations - keep all hooks at the top level
  const [imageRegistry, setImageRegistry] = useState<ImageRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [expandedRooms, setExpandedRooms] = useState<Record<string, boolean>>({});
  
  // Track which rooms have loaded images
  const [loadedRooms, setLoadedRooms] = useState<Record<string, boolean>>({});
  
  // Group images by room - optimized and memoized for better performance
  const roomGroups = React.useMemo(() => {
    // Create a map for better performance with large datasets
    const groups: Record<string, ImageRegistryItem[]> = {};
    
    // Pre-allocate the correct size arrays (optimization)
    // First count how many images per room
    const roomCounts: Record<string, number> = {};
    
    for (let i = 0; i < imageRegistry.length; i++) {
      const roomName = imageRegistry[i].room || 'Other';
      roomCounts[roomName] = (roomCounts[roomName] || 0) + 1;
    }
    
    // Create the arrays with the right size
    Object.keys(roomCounts).forEach(room => {
      groups[room] = [];
    });
    
    // Fill the arrays
    for (let i = 0; i < imageRegistry.length; i++) {
      const img = imageRegistry[i];
      const roomName = img.room || 'Other';
      groups[roomName].push(img);
    }
    
    return groups;
  }, [imageRegistry]);

  // Functions
  // Track which room's images we're viewing
  const [currentRoomName, setCurrentRoomName] = useState<string>('');
  
  // Images for the current room only (filtered to only include those with URLs)
  const currentRoomImages = React.useMemo(() => {
    if (!currentRoomName) return [];
    const roomImages = roomGroups[currentRoomName] || [];
    return roomImages.filter(img => img.url); // Only include images that have URLs loaded
  }, [currentRoomName, roomGroups]);
  
  const openImageModal = (roomName: string, localIndex: number) => {
    setCurrentRoomName(roomName);
    setSelectedImageIndex(localIndex);
    setModalOpen(true);
  };

  // Handle accordion change to lazy load images
  const handleAccordionChange = async (value: string[]) => {
    // Find which rooms are newly expanded
    const newlyExpandedRooms = value.filter(room => !loadedRooms[room]);
    
    // Load images for newly expanded rooms
    for (const roomName of newlyExpandedRooms) {
      await loadImagesForRoom(roomName);
      // Mark this room as loaded
      setLoadedRooms(prev => ({...prev, [roomName]: true}));
    }
    
    // Update expanded rooms state
    const expandedState: Record<string, boolean> = {};
    value.forEach(room => {
      expandedState[room] = true;
    });
    setExpandedRooms(expandedState);
  };
  
  // Function to load images for a specific room
  const loadImagesForRoom = async (roomName: string) => {
    if (!propertyId || !roomGroups[roomName]) return;
    
    // Get the image items for this room
    const roomImages = roomGroups[roomName];
    
    // Create a new registry with updated items
    const updatedRegistry = [...imageRegistry];
    
    // Load URLs for all images in this room
    for (const item of roomImages) {
      if (!item.imageId || item.url) continue; // Skip if no ID or already has URL
      
      try {
        const url = await propertyService.getPropertyImageUrl(propertyId, item.imageId);
        
        if (url) {
          // Find and update this item in the main registry
          const index = updatedRegistry.findIndex(i => i.imageId === item.imageId);
          if (index >= 0) {
            updatedRegistry[index] = {...updatedRegistry[index], url};
          }
        }
      } catch (e) {
        console.error(`Error fetching image URL for ${item.imageId}:`, e);
      }
    }
    
    // Update the registry with the new URLs
    setImageRegistry(updatedRegistry);
  };

  // Data fetching effect - only loads image metadata initially, not actual images
  useEffect(() => {
    const fetchImageRegistry = async () => {
      if (!propertyId || !inspectionId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Fetch inspection registry to get image IDs and metadata
        const inspectionRegistry = await propertyService.getPropertyInspectionRegistry(propertyId) as ImageInspectionRegistry;
        
        if (!inspectionRegistry || !inspectionRegistry.data || inspectionRegistry.data.length === 0) {
          setError('No images found for this inspection');
          setLoading(false);
          return;
        }
        
        // Get only items with images
        const itemsWithImages = inspectionRegistry.data
          .filter((item: ImageInspectionRegistryItem) => item.imageId);
        
        if (itemsWithImages.length === 0) {
          setError('No images found for this inspection');
          setLoading(false);
          return;
        }
        
        // Create array of image registry items (without URLs initially)
        const registryItems: ImageRegistryItem[] = itemsWithImages.map(item => ({
          imageId: item.imageId || '',
          room: item.Room || 'Unknown Room',
          description: item.Description || 'No description available',
          redFlags: item.RedFlags || []
          // url will be loaded later when needed
        }));
        
        setImageRegistry(registryItems);
      } catch (e) {
        console.error('Error fetching inspection images:', e);
        setError('Failed to load inspection registry');
      } finally {
        setLoading(false);
      }
    };
    
    fetchImageRegistry();
  }, [propertyId, inspectionId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (imageRegistry.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">No images available for this inspection</p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <h3 className="text-xl font-medium mb-4">Inspection Images ({imageRegistry.length})</h3>
      
      {/* Images grouped by room in collapsible sections */}
      <Accordion 
        type="multiple" 
        className="space-y-2"
        onValueChange={handleAccordionChange}
      >
        {Object.entries(roomGroups).map(([roomName, roomImages]) => {
          // Count images with issues in this room (not total issues to avoid excessive counts)
          const imagesWithIssues = roomImages.filter(img => img.redFlags && img.redFlags.length > 0).length;
          const isLoaded = loadedRooms[roomName];
          
          return (
            <AccordionItem 
              key={roomName} 
              value={roomName}
              className="border border-gray-200 rounded-md overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                <div className="flex items-center justify-between w-full pr-4">
                  <span className="font-medium">{roomName}</span>
                  <div className="flex items-center gap-3">
                    {imagesWithIssues > 0 && (
                      <Badge variant="destructive" className="rounded-full px-2 py-0 text-xs">
                        {imagesWithIssues} {imagesWithIssues === 1 ? 'Issue' : 'Issues'}
                      </Badge>
                    )}
                    <span className="text-xs text-gray-500">
                      {roomImages.length} {roomImages.length === 1 ? 'image' : 'images'}
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              
              <AccordionContent className="p-2 bg-gray-50">
                {isLoaded ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {roomImages.map((image, idx) => {
                      return (
                        <div 
                          key={idx} 
                          className="relative cursor-pointer rounded-md border border-gray-200 overflow-hidden h-16 bg-white transition-transform hover:scale-[1.02] hover:shadow-sm"
                          onClick={() => openImageModal(roomName, idx)}
                        >
                          {image.url ? (
                            <img
                              src={image.url}
                              alt={`${image.room}`}
                              loading="lazy"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-4 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span className="text-sm text-gray-500">Loading images...</span>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
      
      {/* Room-specific Full-screen Image Viewer */}
      <PropertyImageCarouselModal 
        imageUrls={currentRoomImages.map(img => img.url || '')}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        initialIndex={selectedImageIndex}
        imageInfos={currentRoomImages.map(img => ({
          room: img.room,
          description: img.description,
          redFlags: img.redFlags
        }))}
        roomName={currentRoomName}
      />
    </div>
  );
};

export default InspectionImagesDisplay;